import {noop} from '@sail/utils';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import KillSwitchContext from 'src/internal/killswitch/KillSwitchContext';
import {registerComponentKillCallback} from 'src/public/links/createComponentKillswitchLink';

export default function KillSwitchProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const parentContext = useContext(KillSwitchContext);
  const hasParentContext = parentContext[1] !== noop;

  const [localKilledIds, setKilledIds] = useState(new Set<string>());

  const localKillComponents = useCallback((ids: string[]) => {
    setKilledIds((prev) => {
      const hasNewIds = ids.some((id) => !prev.has(id));

      if (!hasNewIds) {
        return prev;
      }

      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  useEffect(() => {
    if (hasParentContext) {
      // No need to register if we are in a nested provider, our parent will handle it
      return;
    }

    const cleanup = registerComponentKillCallback(localKillComponents);
    return cleanup;
  }, [localKillComponents, hasParentContext]);

  const killedIds = hasParentContext ? parentContext[0] : localKilledIds;
  const killComponents = hasParentContext
    ? parentContext[1]
    : localKillComponents;

  const contextValue = useMemo(
    () => [killedIds, killComponents] as const,
    [killedIds, killComponents],
  );

  return (
    <KillSwitchContext.Provider value={contextValue}>
      {children}
    </KillSwitchContext.Provider>
  );
}
